import React from 'react'
import { SlideOutNavItem } from './SlideOutNavItem'
import {
  SideMenuProvider,
  useSideMenu,
} from 'features/settings/context/SideMenuContext'
import { useAuth } from 'context/authentication/useAuth'
import { featureFlagService } from 'utils/featureFlagService'
import type { TabGroupKeys } from 'features/settings/components/SideMenu'

// Order of settings groups to display
const GROUP_ORDER: TabGroupKeys[] = [
  'company',
  'users',
  'platform',
  'data',
  'notifications',
  'misc',
  'automations',
  'email-outreach',
]

/**
 * Renders the list of settings groups in the slide-out panel.
 * Filters groups based on user permissions and feature flags.
 */
const SettingsSlideOutContentInner: React.FC = React.memo(() => {
  const { tabGroups } = useSideMenu()
  const { user } = useAuth()
  const featureFlags = featureFlagService()

  return (
    <ul className="list-none m-0 p-0 flex flex-col gap-1.5">
      {GROUP_ORDER.map((groupKey) => {
        const group = tabGroups[groupKey]
        if (!group || !group.isAllowed(user, featureFlags)) {
          return null
        }

        const firstAllowedTab = Object.values(group.tabs).find((tab) =>
          tab?.isAllowed(user, featureFlags)
        )
        if (!firstAllowedTab) {
          return null
        }

        const path = `/settings/${group.uri}/${firstAllowedTab.uri}`

        return (
          <SlideOutNavItem
            key={groupKey}
            label={group.title}
            path={path}
            section="settings"
            keepOpen
          />
        )
      })}
    </ul>
  )
})

SettingsSlideOutContentInner.displayName = 'SettingsSlideOutContentInner'

/**
 * Settings slide-out panel content with its own SideMenuProvider context.
 * Memoized to prevent re-renders that cause flickering when the parent re-renders (FIR-5953).
 */
export const SettingsSlideOutContent: React.FC = React.memo(() => {
  return (
    <SideMenuProvider>
      <SettingsSlideOutContentInner />
    </SideMenuProvider>
  )
})

SettingsSlideOutContent.displayName = 'SettingsSlideOutContent'
